import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, FileSearch, Quote } from 'lucide-react';
import { Badge } from '../ui/badge';
import type { KbCitation, KbNodeCitations } from '../../lib/api';
import { formatCitationSource, useSourceLabels } from '../../lib/source-label';
import { formatDate } from '../../lib/utils';

function CitationRow({
  citation, onOpen, index,
}: {
  citation: KbCitation;
  onOpen: (citation: KbCitation) => void;
  /** Matches the inline marker number in the prose, when there is one. */
  index?: number;
}) {
  // "Meeting name · folder" (or "#channel"), composed from the item's own title and the watched
  // source's name — the raw id string only when neither is known.
  const sourceLabels = useSourceLabels();
  return (
    <button
      onClick={() => onOpen(citation)}
      className="w-full text-left px-3 py-2.5 hover:bg-surface-subtle transition-colors group"
    >
      <div className="flex items-start gap-2">
        {index !== undefined ? (
          <span className="text-[10px] font-mono font-semibold text-orange-500 mt-0.5 flex-shrink-0 w-3 text-center">
            {index}
          </span>
        ) : (
          <Quote size={11} className="text-ink-faint mt-0.5 flex-shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-xs text-ink leading-snug">{citation.claim}</span>
          <span className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] text-ink-faint">{formatCitationSource(citation.source, sourceLabels)}</span>
            {citation.source.capturedAt && (
              <span className="text-[10px] text-ink-faint">{formatDate(citation.source.capturedAt)}</span>
            )}
            {citation.confidence < 1 && (
              <span className="text-[10px] text-ink-faint">{Math.round(citation.confidence * 100)}%</span>
            )}
          </span>
        </span>
        <ExternalLink
          size={11}
          className="text-border-strong group-hover:text-orange-500 transition-colors flex-shrink-0 mt-0.5"
        />
      </div>
    </button>
  );
}

/**
 * One citation as a margin note — the card that sits beside the line it backs, Google-Docs-shaped.
 *
 * Deliberately not `CitationRow`: in a 248px column the claim, the source and the date cannot share
 * a line, so the claim gets three lines of its own and the provenance sits beneath it. It opens the
 * same dialog a row in the panel does, so evidence is read the same way wherever it was clicked.
 */
export function CitationMarginCard({
  citation, onOpen,
}: {
  citation: KbCitation;
  onOpen: (citation: KbCitation) => void;
}) {
  const sourceLabels = useSourceLabels();
  return (
    <button
      onClick={() => onOpen(citation)}
      className="w-full text-left font-sans rounded-lg border border-border bg-card px-2.5 py-2 shadow-sm transition-all hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-md group"
    >
      <span className="flex items-start gap-1.5">
        <Quote size={10} className="text-orange-500 mt-[3px] flex-shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] text-ink leading-snug line-clamp-3">{citation.claim}</span>
          <span className="mt-1 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-ink-faint truncate">
              {formatCitationSource(citation.source, sourceLabels)}
            </span>
            {citation.source.capturedAt && (
              <span className="text-[10px] text-ink-faint">{formatDate(citation.source.capturedAt)}</span>
            )}
          </span>
        </span>
        <ExternalLink
          size={10}
          className="text-border-strong group-hover:text-orange-500 transition-colors flex-shrink-0 mt-[3px]"
        />
      </span>
    </button>
  );
}

/**
 * A node's evidence: what backs the text as it stands, and what backed text that has since been
 * rewritten away.
 *
 * The superseded group is the backtracking view — it answers "what did this page used to claim,
 * and on whose authority" without diffing revisions by hand. It stays collapsed because it is
 * history, not current knowledge.
 *
 * Selection is raised to the parent so that a click here and a click on an inline marker in the
 * prose open the same dialog instance.
 */
export function CitationPanel({
  citations, onOpen, currentShownElsewhere = false,
}: {
  citations: KbNodeCitations;
  onOpen: (citation: KbCitation) => void;
  /**
   * True when the current citations are already rendered somewhere else — the diff's margin, in KB
   * Review. The "Supporting this page" group is then dropped rather than shown empty, because its
   * empty state reads "every recorded citation refers to text that has since been rewritten",
   * which would be a plain lie about evidence sitting a few pixels to the right. What is left is
   * the superseded group, which has no anchor in the live body and so cannot be in a margin.
   */
  currentShownElsewhere?: boolean;
}) {
  const [openCurrent, setOpenCurrent] = useState(true);
  const [openSuperseded, setOpenSuperseded] = useState(false);

  // Nothing to add beside the margin — say nothing, rather than an empty frame.
  if (currentShownElsewhere && citations.superseded.length === 0) return null;

  const total = citations.current.length + citations.superseded.length;
  if (total === 0) {
    return (
      <p className="text-[11px] text-ink-faint">
        No citations recorded for this page yet. Content ingested by the agent carries evidence for
        its load-bearing statements; pages written by hand don't.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {!currentShownElsewhere && (
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          onClick={() => setOpenCurrent((o) => !o)}
          className="w-full flex items-center gap-2 px-3 py-2 bg-surface-subtle hover:bg-surface-muted transition-colors"
        >
          {openCurrent ? <ChevronDown size={12} className="text-ink-faint" /> : <ChevronRight size={12} className="text-ink-faint" />}
          <FileSearch size={12} className="text-ink-faint" />
          <span className="text-xs font-medium text-ink flex-1 text-left">Supporting this page</span>
          <Badge variant="success">{citations.current.length}</Badge>
        </button>
        {openCurrent && (
          citations.current.length === 0 ? (
            <p className="px-3 py-3 text-[11px] text-ink-faint">
              Nothing currently cited — every recorded citation refers to text that has since been
              rewritten.
            </p>
          ) : (
            <div className="divide-y divide-border-subtle">
              {citations.current.map((c, i) => (
                <CitationRow key={c.id} citation={c} index={i + 1} onOpen={onOpen} />
              ))}
            </div>
          )
        )}
      </div>
      )}

      {citations.superseded.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setOpenSuperseded((o) => !o)}
            className="w-full flex items-center gap-2 px-3 py-2 bg-surface-subtle hover:bg-surface-muted transition-colors"
          >
            {openSuperseded ? <ChevronDown size={12} className="text-ink-faint" /> : <ChevronRight size={12} className="text-ink-faint" />}
            <AlertTriangle size={12} className="text-ink-faint" />
            <span className="text-xs font-medium text-ink flex-1 text-left">
              Superseded
              <span className="font-normal text-ink-faint"> — backed text this page no longer says</span>
            </span>
            <Badge variant="muted">{citations.superseded.length}</Badge>
          </button>
          {openSuperseded && (
            <div className="divide-y divide-border-subtle opacity-75">
              {citations.superseded.map((c) => (
                <CitationRow key={c.id} citation={c} onOpen={onOpen} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
